export type FileExtension = (typeof allFileExtensions)[number]

export type CommonFileExtensions = (typeof commonFileExtensions)[number]
export type MicrosoftFileExtensions = (typeof microsoftFileExtensions)[number]
export type AudioFileExtensions = (typeof audioFileExtensions)[number]
export type CompressedFileExtensions = (typeof compressedFileExtensions)[number]
export type VideoFileExtensions = (typeof videoFileExtensions)[number]
export type PhotoFileExtensions = (typeof photoFileExtensions)[number]

export const commonFileExtensions = ['.txt', '.rtf', '.csv', '.pdf'] as const
export const microsoftFileExtensions = ['.ppt', '.pptx', '.doc', '.docx', '.xls', '.xlsx'] as const
export const audioFileExtensions = ['.aif', '.mp3', '.ogg', '.wav'] as const
export const compressedFileExtensions = ['.zip', '.gz'] as const
export const videoFileExtensions = ['.mov', '.mp4', '.avi', '.webm'] as const
export const photoFileExtensions = ['.jpg', '.jpeg', '.png', '.svg', '.webp'] as const

export const allFileExtensions = [
    ...commonFileExtensions,
    ...microsoftFileExtensions,
    ...audioFileExtensions,
    ...compressedFileExtensions,
    ...videoFileExtensions,
    ...photoFileExtensions,
]

export const attachmentFileExtensions: FileSelectExtension[] = [
    '.pdf',
    '.ppt',
    '.pptx',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.jpg',
    '.jpeg',
    '.png',
    '.csv',
    '.txt',
]

export const fileSelectExtensions = [
    ...commonFileExtensions,
    ...microsoftFileExtensions,
    ...photoFileExtensions,
    ...videoFileExtensions,
] as const

export type FileSelectExtension = (typeof fileSelectExtensions)[number]

export function parseFilenameForExtension(filename: string): string {
    // dot then any word characters until either end of string or a question mark,
    // if there is a question mark then match word characters until end of string.
    // We need to do this because android adds query strings onto its local file names
    // when selecting from gallery, so we will have names like "file://foo/bar.jpg?1234"
    const match = filename.match(/(\.\w+)(?:\?\w*)?$/i)
    return match != null ? match[1].toLowerCase() : ''
}

export function extractFileExtension(filename: string): FileExtension {
    const ext = parseFilenameForExtension(filename)
    if (!isKnownFileExtension(ext)) {
        throw new UnrecognisedExtensionError(filename, ext)
    }
    return ext
}

/** Remove the final dot and anything after it
 *
 * @example getFilenameWithoutExtension({ name: 'file' }) // 'file'
 * @example getFilenameWithoutExtension({ name: 'file.txt' }) // 'file'
 * @example getFilenameWithoutExtension({ name: 'file.txt.7z' }) // 'file.txt'
 */
export function getFilenameWithoutExtension(file: File): string {
    const lastDotPosition = file.name.lastIndexOf('.')
    if (lastDotPosition === -1) return file.name
    return file.name.substring(0, lastDotPosition)
}

export function fileExtensionFromMimeType(mimeType: string): FileExtension {
    const slashIndex = mimeType.indexOf('/')
    if (slashIndex === -1) return null

    const subtype = mimeType.slice(1 + slashIndex)
    switch (subtype) {
        case 'svg+xml':
            return '.svg'
        case 'quicktime':
            return '.mov'
        case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
            return '.docx'
        case 'vnd.openxmlformats-officedocument.presentationml.presentation':
            return '.pptx'
    }

    return addDotPrefix(subtype)
}

export function isKnownFileExtension(extension: string): extension is FileExtension {
    return allFileExtensions.includes(extension as FileExtension)
}

export function isKnownFileSelectExtension(extension: string): extension is FileSelectExtension {
    return fileSelectExtensions.includes(extension as FileSelectExtension)
}

export function isPhotoExtension(extension: FileExtension): extension is PhotoFileExtensions {
    return photoFileExtensions.includes(extension as PhotoFileExtensions)
}

export function isVideoExtension(extension: FileExtension): extension is VideoFileExtensions {
    return videoFileExtensions.includes(extension as VideoFileExtensions)
}

export function addDotPrefix(extension: string): FileExtension {
    if (extension == null) return null
    const extensionWithDot = `.${extension}`
    return isKnownFileExtension(extensionWithDot) ? extensionWithDot : null
}

export class UnrecognisedExtensionError extends Error {
    constructor(
        public filename: string,
        public extension: string
    ) {
        super(`File ${filename} with extension ${extension} is unrecognised`)
    }
}

export function isUnrecognisedExtensionError(error: any): error is UnrecognisedExtensionError {
    return error instanceof UnrecognisedExtensionError
}
